/**
 * A UTC timestamp string in ISO 8601 format (e.g., "2024-01-15T10:30:00Z")
 * Corresponds to OffsetDateTime in backend, format: date-time in OpenAPI
 */
export type OffsetDateTime = string & { readonly __brand: "OffsetDateTime" };

/**
 * A date-only string in ISO 8601 format (e.g., "2024-01-15")
 * Corresponds to LocalDate in backend, format: date in OpenAPI
 */
export type LocalDate = string & { readonly __brand: "LocalDate" };

/**
 * A time-of-day string in HH:MM or HH:MM:SS format (e.g., "09:30:00")
 * Corresponds to LocalTime in backend, format: partial-time in OpenAPI
 */
export type LocalTime = string & { readonly __brand: "LocalTime" };

// ============ Type assertion helpers ============
//
// Thin wrappers that assert a string value is a branded datetime type.
// Use these instead of inline `as LocalDate` / `as OffsetDateTime` casts
// so that the lint rule `no-datetime-type-assertion` is satisfied.
//
// Production usage: values from third-party library callbacks (e.g. Nivo),
// string manipulation (e.g. `.slice(0, 10)`), or API fields that the
// generated client still types as plain `string`.

/**
 * Assert that a string value is an OffsetDateTime.
 * Use when you have a known-valid timestamp string from a source that
 * doesn't use the branded type (e.g., third-party library callbacks).
 */
export function asOffsetDateTime(value: string): OffsetDateTime {
  return value as OffsetDateTime;
}

/**
 * Assert that a string value is a LocalDate.
 * Use when you have a known-valid date string from a source that
 * doesn't use the branded type (e.g., third-party library callbacks).
 */
export function asLocalDate(value: string): LocalDate {
  return value as LocalDate;
}

/**
 * Assert that a string value is a LocalTime.
 * Use when you have a known-valid time string from a source that
 * doesn't use the branded type (e.g., third-party library callbacks).
 */
export function asLocalTime(value: string): LocalTime {
  return value as LocalTime;
}
